import * as z from "zod/mini";

const STORAGE_KEY = "qrcodeReader.failedUpdates";
const MAX_ENTRIES = 100;

const failedUpdateLogEntrySchema = z.object({
  ticketPath: z.string(),
  ownerUserId: z.string(),
  readerUserId: z.nullable(z.string()),
  errorCode: z.nullable(z.string()),
  errorMessage: z.string(),
  savedAt: z.number(),
});

const failedUpdateLogEntriesSchema = z.array(failedUpdateLogEntrySchema);

export type FailedUpdateLogEntry = z.infer<typeof failedUpdateLogEntrySchema>;

export const getFailedUpdates = (): FailedUpdateLogEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    const result = failedUpdateLogEntriesSchema.safeParse(parsed);
    if (!result.success) {
      console.error("Invalid failed update entries in storage", result.error);
      return [];
    }
    return result.data;
  } catch (e) {
    console.error("Failed to read failed update entries", e);
    return [];
  }
};

export const clearFailedUpdates = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error("Failed to clear failed update entries", e);
  }
};

export const saveFailedUpdate = (entry: {
  ticketPath: string;
  ownerUserId: string;
  readerUserId: string | null;
  error: unknown;
}) => {
  try {
    const errorObj =
      entry.error !== null && typeof entry.error === "object"
        ? (entry.error as { code?: unknown; message?: unknown })
        : {};
    const newEntry: FailedUpdateLogEntry = {
      ticketPath: entry.ticketPath,
      ownerUserId: entry.ownerUserId,
      readerUserId: entry.readerUserId,
      errorCode: typeof errorObj.code === "string" ? errorObj.code : null,
      errorMessage:
        typeof errorObj.message === "string"
          ? errorObj.message
          : String(entry.error),
      savedAt: Date.now(),
    };
    const existing: unknown = JSON.parse(
      localStorage.getItem(STORAGE_KEY) ?? "[]"
    );
    const existingEntries: unknown[] = Array.isArray(existing) ? existing : [];
    const trimmed = [...existingEntries, newEntry].slice(-MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.error("Failed to persist failed update entry", e);
  }
};
